<template>
  <div class="col-lg-12">
    <div class="page-header b-flex justify-content-between mt-3">
      <div class="page-title">{{ $t('api.header') }}</div>
      <b-button v-b-modal.createConfirmationModal variant="success">
        {{ $t('api.create.buttonLabel') }}
      </b-button>
    </div>

    <div class="card">
      <div class="card-body">
        <div v-if="loading" class="dot-opacity-loader">
          <span></span>
          <span></span>
          <span></span>
        </div>

        <div v-else class="row overflow-auto">
          <div class="col-12">
            <b-table
              v-if="tokens"
              id="boxe-list"
              responsive
              show-empty
              small
              stacked="md"
              :items="tokens"
              :fields="fields"
            >
              <template #cell(token)="row" class="align-items-center justify-content-center">
                {{ formatToken(row.item.token) }}
              </template>
              <template #cell(dateCreate)="row" class="align-items-center justify-content-center">
                {{ formatDate(row.item.dateCreate) }}
              </template>
              <template #cell(action)="row">
                <b-btn
                  v-b-modal.showTokenModal
                  variant="info"
                  class="btn-icon m-2"
                  @click="selectedToken = row.item.token"
                >
                  <i class="mdi mdi-magnify btn-icon-prepend"></i>
                </b-btn>
                <b-btn
                  v-clipboard="row.item.token"
                  variant="secondary"
                  class="btn-icon m-2"
                  @success="handleSuccess"
                  @error="handleError"
                >
                  <i class="mdi mdi-content-copy btn-icon-prepend"></i>
                </b-btn>
                <b-btn
                  v-b-modal.revokeConfirmationModal
                  variant="danger"
                  class="btn-icon m-2"
                  @click="selectedToken = row.item.token"
                >
                  <i class="mdi mdi-close btn-icon-prepend"></i>
                </b-btn>
              </template>
            </b-table>
          </div>
        </div>
      </div>
    </div>
    <div class="mt-3">
      <b-button variant="primary" href="/ext-api/swagger/">
        {{ $t('api.documentation.label') }}
      </b-button>
    </div>
    <b-modal
      id="createConfirmationModal"
      centered
      size="sm"
      close-disable
      hide-header-close
      ok-variant="success"
      hide-header
      body-class="text-center"
      :cancel-title="$t('button.cancel.label')"
      lazy
      @ok="createToken"
    >
      {{ $t('api.create.confirmation') }}
    </b-modal>
    <b-modal
      id="showTokenModal"
      centered
      size="md"
      close-disable
      hide-header-close
      hide-header
      ok-only
      body-class="text-center"
      lazy
    >
      {{ selectedToken }}
    </b-modal>
    <delete-confirmation-modal
      modal-id="revokeConfirmationModal"
      @ok="confirmRevokeToken"
      @hidden="onHideRevokeToken"
    />
  </div>
</template>

<script>
import createToken from '@/api/tokens/mutations/createToken.gql';
import revokeToken from '@/api/tokens/mutations/revokeToken.gql';
import apiTokens from '@/api/tokens/apiTokens.gql';
import moment from 'moment';

export default {
  name: 'ApiTokens',
  apollo: {
    tokens: {
      query: apiTokens,
      update(data) {
        const queryData = data[Object.keys(data)[0]];
        return (queryData || {}).apiTokens || [];
      },
    },
  },
  data: () => {
    return {
      selectedToken: '',
    };
  },
  computed: {
    loading() {
      return this.$apollo.queries.tokens.loading;
    },
    fields() {
      return [
        {
          key: 'token',
          label: this.$t('api.fields.token'),
          sortable: false,
        },
        {
          key: 'dateCreate',
          label: this.$t('api.fields.dateCreate'),
          sortable: false,
        },
        {
          key: 'userName',
          label: this.$t('api.fields.userName'),
          sortable: false,
        },
        {
          key: 'objectName',
          label: this.$t('api.fields.objectName'),
          sortable: false,
        },
        {
          key: 'action',
          label: '',
          sortable: false,
          class: 'text-center',
        },
      ];
    },
    apiTokensQuery() {
      return {
        query: apiTokens,
      };
    },
  },
  methods: {
    createToken() {
      this.$apollo
        .mutate({
          mutation: createToken,
          refetchQueries: [this.apiTokensQuery],
        })
        .then((result) => {
          const data = ((result.data || {}).koeeBoxMutation || {}).createToken || {};
          if (data.success && data.token) {
            this.$bvToast.toast(this.$t('api.toast.createSuccess'), {
              title: this.$t('toast.success'),
              variant: 'success',
              solid: true,
            });
          } else {
            this.$bvToast.toast(this.$t('api.toast.createError'), {
              title: this.$t('toast.error'),
              variant: 'danger',
              solid: true,
            });
          }
        })
        .catch((e) => {
          this.$bvToast.toast(this.$t('api.toast.createError'), {
            title: this.$t('toast.error'),
            variant: 'danger',
            solid: true,
          });
          console.log(e.message);
        });
    },
    revokeToken(token) {
      this.$apollo
        .mutate({
          mutation: revokeToken,
          variables: {
            token,
          },
          refetchQueries: [this.apiTokensQuery],
        })
        .then((result) => {
          const data = ((result.data || {}).koeeBoxMutation || {}).revokeToken || {};
          if (data.success) {
            this.$bvToast.toast(this.$t('api.toast.revokeSuccess'), {
              title: this.$t('toast.success'),
              variant: 'success',
              solid: true,
            });
          } else {
            this.$bvToast.toast(this.$t('api.toast.revokeError'), {
              title: this.$t('toast.error'),
              variant: 'danger',
              solid: true,
            });
          }
        })
        .catch((e) => {
          this.$bvToast.toast(this.$t('api.toast.revokeError'), {
            title: this.$t('toast.error'),
            variant: 'danger',
            solid: true,
          });
          console.log(e.message);
        });
    },
    confirmRevokeToken() {
      this.revokeToken(this.selectedToken);
    },
    onHideRevokeToken() {
      this.selectedToken = '';
    },
    formatDate(date) {
      return moment(date).format('DD.MM.YYYY HH:mm:ss');
    },
    formatToken(token) {
      return token.substring(0, 7) + '...';
    },
    handleSuccess() {
      this.$bvToast.toast(this.$t('toast.copySuccess'), {
        title: this.$t('toast.success'),
        variant: 'success',
        solid: true,
      });
    },
    handleError() {
      this.$bvToast.toast(this.$t('toast.copyFailed'), {
        title: this.$t('toast.error'),
        variant: 'danger',
        solid: true,
      });
    },
  },
};
</script>
